import { useAuth } from '@berky/auth'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useFeedbackApi } from './feedback-api'

const THRESHOLD_MS = 5 * 60 * 1000
const RECENT_ACTIVITY_MS = 60 * 1000
const DISPLAY_MS = 5 * 60 * 1000
const OPEN_OVERLAY_SELECTOR = [
  '[data-slot="dialog-content"][data-state="open"]',
  '[data-slot="sheet-content"][data-state="open"]',
  '[data-slot="alert-dialog-content"][data-state="open"]',
  '[data-slot="dropdown-menu-content"][data-state="open"]',
  '[data-slot="dropdown-menu-sub-content"][data-state="open"]',
  '[data-slot="popover-content"][data-state="open"]',
  '[data-slot="select-content"][data-state="open"]',
].join(',')

function berlinDay(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const value = (type: string) => parts.find((part) => part.type === type)?.value
  return `${value('year')}-${value('month')}-${value('day')}`
}

function canDisplay(blocked: boolean): boolean {
  return document.visibilityState === 'visible' &&
    !blocked &&
    !document.querySelector(OPEN_OVERLAY_SELECTOR)
}

/** Local activity only; the server stores one reservation per user and Berlin day. */
export function useFeedbackReminder(blocked: boolean) {
  const { user } = useAuth()
  const userId = user?.id
  const api = useFeedbackApi()
  const apiRef = useRef(api)
  const [visible, setVisible] = useState(false)
  const blockedRef = useRef(blocked)
  const dismissRef = useRef<() => void>(() => {})
  useEffect(() => { blockedRef.current = blocked }, [blocked])
  useEffect(() => { apiRef.current = api }, [api])

  useEffect(() => {
    if (!userId) return

    let day = berlinDay()
    let key = `feedback-reminder:${userId}:${day}`
    const readElapsed = () => {
      try {
        return Math.min(THRESHOLD_MS, Math.max(0, Number(sessionStorage.getItem(key)) || 0))
      } catch {
        return 0
      }
    }
    let elapsed = readElapsed()
    let lastTick = performance.now()
    let lastActivity = Number.NEGATIVE_INFINITY
    let claiming = false
    let settled = false
    let pending = false
    let showing = false
    let retryAfter = 0
    let displayTimer: ReturnType<typeof setTimeout> | undefined
    let displayStartedAt = 0
    let displayRemaining = DISPLAY_MS
    let active = true

    const hide = () => {
      if (displayTimer) clearTimeout(displayTimer)
      displayTimer = undefined
      if (showing) {
        displayRemaining = Math.max(0, displayRemaining - (performance.now() - displayStartedAt))
      }
      showing = false
      setVisible(false)
    }
    dismissRef.current = () => {
      settled = true
      pending = false
      hide()
    }
    const interact = () => { lastActivity = performance.now() }
    const onVisibilityChange = () => {
      // Never charge time spent in a hidden tab, even if it returns within 60 seconds.
      lastTick = performance.now()
      tick()
    }

    const tick = () => {
      if (!active) return
      const now = performance.now()
      const currentDay = berlinDay()
      if (currentDay !== day) {
        day = currentDay
        key = `feedback-reminder:${userId}:${day}`
        elapsed = readElapsed()
        lastTick = now
        settled = false
        pending = false
        hide()
        displayRemaining = DISPLAY_MS
      }
      if (document.visibilityState === 'visible') {
        const increment = Math.max(0, Math.min(now, lastActivity + RECENT_ACTIVITY_MS) - lastTick)
        if (increment > 0 && elapsed < THRESHOLD_MS) {
          elapsed = Math.min(THRESHOLD_MS, elapsed + increment)
          try { sessionStorage.setItem(key, String(elapsed)) } catch { /* Storage may be disabled. */ }
        }
      }
      lastTick = now

      if (showing && !canDisplay(blockedRef.current)) {
        hide()
        pending = true
      }
      if (pending && canDisplay(blockedRef.current)) {
        pending = false
        showing = true
        displayStartedAt = now
        setVisible(true)
        displayTimer = setTimeout(() => {
          settled = true
          hide()
        }, displayRemaining)
      }
      if (elapsed < THRESHOLD_MS || settled || claiming || pending ||
          showing || now < retryAfter || !canDisplay(blockedRef.current)) return

      claiming = true
      void apiRef.current.post<{ show: boolean; day: string }>('/reminder/claim', {})
        .then((result) => {
          if (!active || settled) return
          if (result.day !== day) {
            retryAfter = performance.now() + 60_000
            return
          }
          settled = true
          if (result.show) {
            pending = true
            tick()
          }
        })
        .catch(() => {
          // Network errors stay silent and cannot cause a rapid claim loop.
          retryAfter = performance.now() + 60_000
        })
        .finally(() => { claiming = false })
    }

    document.addEventListener('pointerdown', interact, { passive: true })
    document.addEventListener('keydown', interact)
    document.addEventListener('scroll', interact, { passive: true, capture: true })
    document.addEventListener('visibilitychange', onVisibilityChange)
    const interval = setInterval(tick, 1000)
    return () => {
      active = false
      clearInterval(interval)
      hide()
      dismissRef.current = () => {}
      document.removeEventListener('pointerdown', interact)
      document.removeEventListener('keydown', interact)
      document.removeEventListener('scroll', interact, true)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [userId])

  const dismiss = useCallback(() => dismissRef.current(), [])
  return { visible, dismiss, suppress: dismiss }
}
